/**
 * Edit Workspace Standard Dialog
 *
 * Modal for editing a workspace standard's fileName and description.
 */

import { useState, useCallback, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { X, Save } from "lucide-react";
import { Button, Input, Textarea } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

interface EditWorkspaceStandardDialogProps {
  open: boolean;
  onClose: () => void;
  standard: {
    _id: Id<"officialStandards">;
    fileName: string;
    description?: string;
  } | null;
}

export function EditWorkspaceStandardDialog({
  open,
  onClose,
  standard,
}: EditWorkspaceStandardDialogProps) {
  const { t } = useTranslation("common");

  const [fileName, setFileName] = useState(standard?.fileName ?? "");
  const [description, setDescription] = useState(standard?.description ?? "");

  const editMutation = useApiMutation(
    api.workspace_standards_mutations.update,
    {
      name: "edit-workspace-standard",
      successMessage: t("workspaceStandards.edit.saved"),
    },
  );

  // Sync state when standard prop changes
  useEffect(() => {
    if (open && standard) {
      setFileName(standard.fileName);
      setDescription(standard.description ?? "");
    }
  }, [open, standard]);

  const handleClose = useCallback(() => {
    if (editMutation.isPending) return;
    onClose();
  }, [editMutation.isPending, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!standard) return;
    const trimmedFileName = fileName.trim();
    if (!trimmedFileName) return;

    await editMutation.mutateAsync({
      standardId: standard._id,
      fileName: trimmedFileName,
      description: description.trim() || undefined,
    });

    onClose();
  }, [fileName, description, standard, editMutation, onClose]);

  const isFormValid = fileName.trim().length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-full max-w-[480px] rounded-[12px] bg-card shadow-xl"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <Dialog.Title className="text-[15px] font-semibold text-primary">
                      {t("workspaceStandards.edit.title")}
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="p-1 rounded-md hover:bg-card-hover transition-colors"
                        aria-label="Close dialog"
                      >
                        <X size={16} className="text-muted" />
                      </button>
                    </Dialog.Close>
                  </div>

                  {/* Body */}
                  <div className="p-5 space-y-4">
                    {/* File Name */}
                    <div>
                      <label
                        htmlFor="edit-ws-standard-filename"
                        className="block text-[13px] font-medium text-secondary mb-1.5"
                      >
                        {t("workspaceStandards.edit.fileName")}
                      </label>
                      <Input
                        id="edit-ws-standard-filename"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        disabled={editMutation.isPending}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label
                        htmlFor="edit-ws-standard-description"
                        className="block text-[13px] font-medium text-secondary mb-1.5"
                      >
                        {t("workspaceStandards.edit.description")}
                      </label>
                      <Textarea
                        id="edit-ws-standard-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t(
                          "workspaceStandards.edit.descriptionPlaceholder",
                        )}
                        disabled={editMutation.isPending}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
                    <Button
                      variant="secondary"
                      onClick={handleClose}
                      disabled={editMutation.isPending}
                    >
                      {t("actions.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={!isFormValid || editMutation.isPending}
                      loading={editMutation.isPending}
                      icon={Save}
                    >
                      {t("workspaceStandards.edit.save")}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
